import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { NavigationText } from '../../../../core/constants/navigation-text';
import { RouteConstants } from '../../../../core/constants/routes';
import { openUrlInNewTab } from '../../../../core/functions/window-helper';
import { ApplicationState } from '../../../../core/models/application-state/application-state';
import { ReturnRouteService } from '../../../../core/services/navigation/return-route.service';
import { PublicAppConfiguration } from '../../../../core/services/public-app-configuration/models/public-app-configuration';
import { PublicAppConfigurationService } from '../../../../core/services/public-app-configuration/public-app-configuration.service';
import { StepLink } from '../../../../modules/application/shared/models/step-link';
import { ApplicationEventActionButtonVisibilityService } from '../../../../modules/application/shared/services/application-event-action-button-visibility/application-event-action-button-visibility.service';
import { ApplicationEventActionService } from '../../../../modules/application/shared/services/application-event-action/application-event-action.service';
import { ApplicationStateService } from '../../../../modules/application/shared/services/application-state/application-state.service';
import { StepNavigationButtonVisibilityService } from '../../../../modules/application/shared/services/step-navigation-button-visibility/step-navigation-button-visibility.service';
import { StepNavigationButtonService } from '../../../../modules/application/shared/services/step-navigation-button/step-navigation-button.service';
import { StepNavigationService } from '../../../../modules/application/shared/services/step-navigation/step-navigation.service';
import { ApplicationTemplateFormDialogService } from '../../../../modules/application/template/shared/services/application-template-form-dialog/application-template-form-dialog.service';
import { ButtonMenuOption } from '../../button-menu/models/button-menu-option';
import { NavigationOptions } from '../../page-header-nav/models/navigation-options';
import { PageHeaderNavItem } from '../../page-header-nav/models/page-header-nav-item';
import { PageHeaderNavComponent } from '../../page-header-nav/page-header-nav.component';

@Component({
    selector: 'app-application-navigation-bar',
    templateUrl: './application-navigation-bar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonModule, PageHeaderNavComponent],
})
export class ApplicationNavigationBarComponent implements OnInit, OnDestroy {
    private readonly router: Router = inject(Router);
    private readonly stepNavigationService: StepNavigationService = inject(
        StepNavigationService,
    );
    private readonly stepNavigationButtonService: StepNavigationButtonService =
        inject(StepNavigationButtonService);
    private readonly stepNavigationOptionsService: StepNavigationButtonVisibilityService =
        inject(StepNavigationButtonVisibilityService);
    private readonly applicationStateService: ApplicationStateService = inject(
        ApplicationStateService,
    );
    private readonly applicationActionService: ApplicationEventActionService =
        inject(ApplicationEventActionService);
    private readonly applicationTemplateFormDialogService: ApplicationTemplateFormDialogService =
        inject(ApplicationTemplateFormDialogService);
    private readonly applicationEventActionButtonVisibilityService: ApplicationEventActionButtonVisibilityService =
        inject(ApplicationEventActionButtonVisibilityService);
    private readonly returnRouteService: ReturnRouteService =
        inject(ReturnRouteService);
    private readonly publicAppConfigurationService: PublicAppConfigurationService =
        inject(PublicAppConfigurationService);

    navItems: NavigationOptions = {};
    applicationState: ApplicationState | null = null;

    public title: string | undefined;

    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.subscribeToStepChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    previous() {
        this.stepNavigationService.triggerPreviousClick();
    }

    next() {
        this.stepNavigationService.triggerNextClick();
    }

    saveAndClose() {
        this.stepNavigationService.triggerSaveAndCloseClick();
    }

    handoverToEwa() {
        this.stepNavigationService.triggerHandoverToEwaClick();
    }

    submitToFcc() {
        this.stepNavigationService.triggerSubmitToFccClick();
    }

    submitToPsca() {
        this.stepNavigationService.triggerSubmitToPscaClick();
    }

    submitToFiveDayHold() {
        this.stepNavigationService.triggerSubmitToFiveDayHoldClick();
    }

    createTemplate() {
        this.applicationActionService.triggerCreateTemplateClick();
    }

    redirectToCevoTools(): void {
        this.publicAppConfigurationService
            .getPublicAppConfiguration()
            .pipe(
                tap((config: PublicAppConfiguration) => {
                    openUrlInNewTab(`${config.cevoToolsUrl}/tools`);
                }),
            )
            .subscribe();
    }

    redirectToCallSignSearch(): void {
        this.publicAppConfigurationService
            .getPublicAppConfiguration()
            .pipe(
                tap((config: PublicAppConfiguration) => {
                    openUrlInNewTab(`${config.cevoToolsUrl}/call-sign-search`);
                }),
            )
            .subscribe();
    }

    redirectToHAATAndMaxERPCalculator(): void {
        this.publicAppConfigurationService
            .getPublicAppConfiguration()
            .pipe(
                tap((config: PublicAppConfiguration) => {
                    openUrlInNewTab(
                        `${config.cevoToolsUrl}/elevation-haat-max-erp`,
                    );
                }),
            )
            .subscribe();
    }

    redirectToDistanceConversion(): void {
        this.publicAppConfigurationService
            .getPublicAppConfiguration()
            .pipe(
                tap((config: PublicAppConfiguration) => {
                    openUrlInNewTab(
                        `${config.cevoToolsUrl}/distance-conversion`,
                    );
                }),
            )
            .subscribe();
    }

    delete() {
        this.applicationActionService.triggerDeleteClick();
    }

    cancel() {
        const returnRoute = this.returnRouteService.consume();
        if (returnRoute) {
            this.router.navigate([returnRoute]);
        } else {
            this.router.navigate([
                RouteConstants.Dashboard.getFullRoute('INDEX'),
            ]);
        }
    }

    private subscribeToStepChanges(): void {
        this.stepNavigationService.currentStep$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (currentStep): currentStep is StepLink =>
                        currentStep !== undefined,
                ),
                tap((currentStep: StepLink) => {
                    this.applicationState =
                        this.applicationStateService.getCurrentApplicationState();
                    this.title = currentStep.title;
                    this.setNavigationLinks(currentStep);
                }),
            )
            .subscribe();
    }

    private setNavigationLinks(stepLink: StepLink) {
        this.setPrimaryLinks(stepLink);
        this.setSecondaryLinks(stepLink);
        this.setTertiaryLinks(stepLink);
    }

    private setTertiaryLinks(stepLink: StepLink) {
        const tertiaryLinks: PageHeaderNavItem[] = [];

        if (
            this.stepNavigationOptionsService.needsToolsMenu(
                stepLink,
                this.applicationStateService.IsApplicationTemplate(),
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.TOOLS_MENU,
                    undefined,
                    this.buildToolsMenuOptions(stepLink),
                ),
            );
        }

        if (
            this.applicationEventActionButtonVisibilityService.needsModifyTemplateButton(
                this.applicationStateService.IsApplicationTemplate(),
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.UPDATE_TEMPLATE_INFO,
                    () => {
                        this.applicationTemplateFormDialogService.displayModificationFormDialog();
                    },
                ),
            );
        }

        tertiaryLinks.push(
            this.stepNavigationButtonService.createTertiaryLink(
                NavigationText.CANCEL,
                this.cancel.bind(this),
            ),
        );

        if (
            this.applicationState?.handoverToEwa != null &&
            this.applicationEventActionButtonVisibilityService.needsDeleteButton(
                stepLink,
                this.applicationState.handoverToEwa,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.DELETE,
                    this.delete.bind(this),
                ),
            );
        }

        if (
            this.applicationState?.handoverToEwa != null &&
            this.stepNavigationOptionsService.needsSaveAndCloseButton(
                stepLink,
                this.applicationState.handoverToEwa,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.SAVE_AND_CLOSE,
                    this.saveAndClose.bind(this),
                ),
            );
        }

        this.navItems.tertiaries = tertiaryLinks;
    }

    private setSecondaryLinks(stepLink: StepLink) {
        const secondaryLinks: PageHeaderNavItem[] = [];

        if (
            this.applicationState &&
            this.applicationState.handoverToEwa != null &&
            this.stepNavigationOptionsService.needsHandoverToEwaButton(
                stepLink,
                this.applicationStateService.IsApplicationTemplate(),
                this.applicationState.handoverToEwa,
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createSecondaryLink(
                    NavigationText.HANDOVER_TO_EWA,
                    this.handoverToEwa.bind(this),
                ),
            );
        }

        if (
            this.stepNavigationOptionsService.needsSubmitMenu(
                stepLink,
                this.applicationStateService.IsApplicationTemplate(),
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createSecondaryLink(
                    NavigationText.SUBMIT_MENU,
                    undefined,
                    this.buildSubmitMenuOptions(stepLink),
                ),
            );
        }

        this.navItems.secondaries = secondaryLinks;
    }

    private setPrimaryLinks(stepLink: StepLink) {
        const primaryLinks: PageHeaderNavItem[] = [];

        if (this.stepNavigationOptionsService.needsPreviousButton(stepLink)) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.PREVIOUS,
                    this.previous.bind(this),
                ),
            );
        }

        if (this.stepNavigationOptionsService.needsNextButton(stepLink)) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.NEXT,
                    this.next.bind(this),
                ),
            );
        }

        this.navItems.primaries = primaryLinks;
    }

    private buildSubmitMenuOptions(stepLink: StepLink): ButtonMenuOption[] {
        const menuOptions: ButtonMenuOption[] = [
            {
                title: NavigationText.SUBMIT_TO_FCC,
                action: this.submitToFcc.bind(this),
                disabled:
                    !this.stepNavigationOptionsService.isSubmitToFccMenuEnabled(
                        this.applicationState?.filingStatus,
                    ),
            },
        ];

        if (
            this.stepNavigationOptionsService.needsSubmitToPscaMenu(
                stepLink,
                this.applicationState,
            )
        ) {
            menuOptions.push({
                title: NavigationText.SUBMIT_TO_PSCA,
                action: this.submitToPsca.bind(this),
                disabled:
                    !this.stepNavigationOptionsService.isSubmitToPscaMenuEnabled(
                        this.applicationState,
                    ),
            });
        }

        menuOptions.push({
            title: NavigationText.SUBMIT_TO_5_DAY_HOLD,
            action: this.submitToFiveDayHold.bind(this),
            disabled:
                !this.stepNavigationOptionsService.isSubmitToFiveDayHoldMenuEnabled(
                    this.applicationState,
                ),
        });

        return menuOptions;
    }

    private buildToolsMenuOptions(stepLink: StepLink): ButtonMenuOption[] {
        const menuOptions: ButtonMenuOption[] = [];

        if (
            this.applicationState &&
            this.applicationEventActionButtonVisibilityService.needsCreateTemplateButton(
                stepLink,
                this.applicationState.isTemplateCreationAllowed,
                this.applicationStateService.IsApplicationTemplate(),
            )
        ) {
            menuOptions.push({
                title: NavigationText.CREATE_TEMPLATE,
                action: this.createTemplate.bind(this),
                disabled: false,
            });
        }

        menuOptions.push({
            title: NavigationText.CEVO_TOOLS,
            action: this.redirectToCevoTools.bind(this),
            disabled: false,
        });

        menuOptions.push({
            title: NavigationText.CALL_SIGN_SEARCH,
            action: this.redirectToCallSignSearch.bind(this),
            disabled: false,
        });

        menuOptions.push({
            title: NavigationText.HAAT_AND_MAX_ERP_CALCULATOR,
            action: this.redirectToHAATAndMaxERPCalculator.bind(this),
            disabled: false,
        });

        menuOptions.push({
            title: NavigationText.DISTANCE_CONVERSION,
            action: this.redirectToDistanceConversion.bind(this),
            disabled: false,
        });

        return menuOptions;
    }
}
